import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api } from './api'
import { authClient } from './better-auth'

export type Role = 'owner' | 'member' | 'actor' | 'supervisor'

export interface AuthUser {
  id: string
  email: string
  name?: string | null
  image?: string | null
  user_metadata: {
    primary_role?: Role
    org_id?: string
  }
}

export interface AuthSession {
  user: AuthUser
}

export interface SignInWithEmailOptions {
  inviteToken?: string
  redirectTo?: string
}

export interface AuthContextValue {
  session: AuthSession | null
  user: AuthUser | null
  role: Role | null
  loading: boolean
  signInWithEmail: (email: string, opts?: SignInWithEmailOptions) => Promise<void>
  signInWithProvider: (provider: 'google', opts?: SignInWithEmailOptions) => Promise<void>
  signOut: () => Promise<void>
}

interface MeResponse {
  id: string
  email: string
  name: string | null
  primary_role: string
  organization: { id: string }
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function callbackUrl(opts?: SignInWithEmailOptions): string {
  const params = new URLSearchParams()
  if (opts?.redirectTo) params.set('next', opts.redirectTo)
  if (opts?.inviteToken) params.set('invite', opts.inviteToken)
  const query = params.toString()
  return `${window.location.origin}/auth/callback${query ? `?${query}` : ''}`
}

function asRole(value: string | undefined): Role | null {
  if (value === 'owner' || value === 'member' || value === 'actor' || value === 'supervisor') {
    return value
  }
  return null
}

/** Better Auth owns the HttpOnly cookie; `/api/v1/me` adds role/org context. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const authState = authClient.useSession()
  const authenticatedUserId = authState.data?.user?.id
  const [profile, setProfile] = useState<MeResponse | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)

  useEffect(() => {
    if (!authenticatedUserId) {
      setProfile(null)
      setProfileLoading(false)
      return
    }
    const controller = new AbortController()
    setProfile(null)
    setProfileLoading(true)
    // Use the shared API client so a 0%-traffic preview continues to carry
    // the Cloudflare version override after Better Auth establishes a
    // session. A raw fetch here silently fell back to the legacy 100% version.
    void api<MeResponse>('/api/v1/me', { signal: controller.signal })
      .then((value) => setProfile(value))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setProfile(null)
      })
      .finally(() => {
        if (!controller.signal.aborted) setProfileLoading(false)
      })
    return () => controller.abort()
  }, [authenticatedUserId])

  const signInWithEmail = useCallback(async (email: string, opts?: SignInWithEmailOptions) => {
    const result = await authClient.signIn.magicLink({
      email,
      callbackURL: callbackUrl(opts),
      errorCallbackURL: `${window.location.origin}/login`,
    })
    if (result.error) throw new Error(result.error.message ?? 'Could not send sign-in link')
  }, [])

  const signInWithProvider = useCallback(
    async (provider: 'google', opts?: SignInWithEmailOptions) => {
      const result = await authClient.signIn.social({
        provider,
        callbackURL: callbackUrl(opts),
        errorCallbackURL: `${window.location.origin}/login`,
      })
      if (result.error) throw new Error(result.error.message ?? 'Could not start Google sign-in')
    },
    [],
  )

  const signOut = useCallback(async () => {
    const result = await authClient.signOut()
    if (result.error) throw new Error(result.error.message ?? 'Could not sign out')
    setProfile(null)
  }, [])

  const role = asRole(profile?.primary_role)
  const user = useMemo<AuthUser | null>(() => {
    const base = authState.data?.user
    if (!base) return null
    return {
      id: base.id,
      email: base.email,
      name: profile?.name ?? base.name,
      image: base.image,
      user_metadata: {
        primary_role: role ?? undefined,
        org_id: profile?.organization.id,
      },
    }
  }, [authState.data?.user, profile, role])

  const session = useMemo<AuthSession | null>(() => (user ? { user } : null), [user])
  // A failed profile request must not leave the entire router suspended
  // forever. Role guards will reject a session with no profile while the
  // next auth refresh retries the request.
  const loading = authState.isPending || profileLoading
  const value = useMemo<AuthContextValue>(
    () => ({ session, user, role, loading, signInWithEmail, signInWithProvider, signOut }),
    [session, user, role, loading, signInWithEmail, signInWithProvider, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside AuthProvider')
  return ctx
}

export function useRequireRole(allowed: readonly Role[]): Role {
  const { role, loading } = useAuth()
  if (loading) throw new Promise(() => {})
  if (!role || !allowed.includes(role)) {
    throw new NotAuthorizedError(`requires one of: ${allowed.join(', ')}; got: ${role ?? 'none'}`)
  }
  return role
}

export class NotAuthorizedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NotAuthorizedError'
  }
}
